import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, useNavigate, useLocation, useParams } from "react-router-dom";
import { UsersPageContainer } from "./Users.styled";
import { Overview, RolesAndPermissions, Groups, ActivityHistory } from "./pages";
import { Header, Tabs } from "./components";
import { UserDetailsTabsEnum } from "./enums/userDetailsTabs";
import { getUserByUuid } from "../../services";

const Users = ({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [selectedTab, setSelectedTab] = useState(UserDetailsTabsEnum.OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  
  const userName = userData?.full_name || location.state?.userName || "User";

  const fetchUserData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getUserByUuid(userId);
      const statusCode = response.status_code || response.statusCode || 200;
      const user = response.body;

      if (statusCode >= 200 && statusCode < 300 && user) {
        setUserData(user);
      } else {
        setError(new Error('Failed to fetch user data'));
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Mapeo de tabs a rutas URL
  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case UserDetailsTabsEnum.OVERVIEW:
        return "";
      case UserDetailsTabsEnum.ROLES_AND_PERMISSIONS:
        return "roles-and-permissions";
      case UserDetailsTabsEnum.GROUPS:
        return "groups";
      case UserDetailsTabsEnum.ACTIVITY_HISTORY:
        return "activity-history";
      default:
        return "";
    }
  };

  const getTabFromRoute = (pathname) => {
    if (pathname.includes("/roles-and-permissions")) {
      return UserDetailsTabsEnum.ROLES_AND_PERMISSIONS;
    } else if (pathname.includes("/groups")) {
      return UserDetailsTabsEnum.GROUPS;
    } else if (pathname.includes("/activity-history")) {
      return UserDetailsTabsEnum.ACTIVITY_HISTORY;
    } else {
      return UserDetailsTabsEnum.OVERVIEW;
    }
  };

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
  }, [location.pathname]);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.("Users", userName, {
      pageHref: "/v2/user-management",
      subHref: location.pathname,
    });
  }, [breadcrumbHelpers, location.pathname, userName]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    
    // Extraer el base path hasta el userId
    const pathParts = location.pathname.split('/').filter(Boolean);
    const usersIndex = pathParts.indexOf('users');
    const basePath = '/' + pathParts.slice(0, usersIndex + 2).join('/');
    
    if (newRoute) {
      navigate(`${basePath}/${newRoute}`, { replace: true });
    } else {
      navigate(basePath, { replace: true });
    }
  };

  return (
    <UsersPageContainer>
      <Header title={userName} email={userData?.email} />
      <Tabs key={selectedTab} onTabChange={handleTabChange} defaultIndex={selectedTab} />
      {selectedTab === UserDetailsTabsEnum.OVERVIEW ? (
        <Overview userData={userData} loading={loading} error={error} />
      ) : (
        <Routes>
          <Route path="roles-and-permissions" element={<RolesAndPermissions />} />
          <Route path="groups" element={<Groups />} />
          <Route path="activity-history" element={<ActivityHistory />} />
        </Routes>
      )}
    </UsersPageContainer>
  );
};

export default Users;

